<template>
  <div class="kpi-card">
    <!-- Database Size -->
    <div class="kpi-row">
      <div class="kpi-left">
        <span class="kpi-icon"><i class="fas fa-database"></i></span>
        <span class="kpi-label">Current Database Size</span>
      </div>
      <div class="kpi-value">{{ displayDbSize }}</div>
    </div>

    <!-- Projects -->
    <div class="kpi-row">
      <div class="kpi-left">
        <span class="kpi-icon"><i class="fas fa-folder-open"></i></span>
        <span class="kpi-label">Projects</span>
      </div>
      <div class="kpi-value">
        <span v-if="loading">—</span>
        <span v-else>{{ formatInt(displayProjectCount) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { PROJECT_DATA_URL } from "@/assets.js";
import { CURRENT_DATABASE_SIZE, formatStorageSize } from "@/site_stats.js";

export default {
  name: "Dashboard",

  data: function () {
    return {
      // target values
      currentDbSize: CURRENT_DATABASE_SIZE,
      projectCount: 0,

      // animated display values
      displayDbSizeValue: 0,
      displayProjectCount: 0,

      // loading state
      loading: true,

      // animation
      animDurationMs: 900,
      animDbId: null,
      animProjId: null
    };
  },

  computed: {
    displayDbSize: function () {
      return formatStorageSize({
        value: this.displayDbSizeValue,
        unit: this.currentDbSize.unit,
        fractionDigits: this.currentDbSize.fractionDigits
      });
    }
  },

  mounted: function () {
    // animate DB size immediately
    this.animateNumber({
      from: 0,
      to: this.currentDbSize.value,
      duration: this.animDurationMs,
      onUpdate: (v) => (this.displayDbSizeValue = v),
      storeId: "animDbId",
      integer: false
    });

    // fetch projects then animate
    this.fetchProjectCount();
  },

  beforeDestroy: function () {
    if (this.animDbId) cancelAnimationFrame(this.animDbId);
    if (this.animProjId) cancelAnimationFrame(this.animProjId);
  },

  methods: {
    fetchProjectCount: async function () {
      try {
        var resp = await fetch(PROJECT_DATA_URL);
        var data = await resp.json();
        var projects = Object.values(data).filter(function (p) {
          return p.status === "live";
        });

        this.projectCount = projects.length;

        this.animateNumber({
          from: 0,
          to: this.projectCount,
          duration: this.animDurationMs,
          onUpdate: (v) => (this.displayProjectCount = v),
          storeId: "animProjId",
          integer: true
        });
      } catch (e) {
        console.error("Failed to load project count:", e);
      } finally {
        this.loading = false;
      }
    },

    animateNumber: function ({ from, to, duration, onUpdate, storeId, integer }) {
      if (this[storeId]) cancelAnimationFrame(this[storeId]);

      var startTime = null;
      var easeOutCubic = function (t) {
        return 1 - Math.pow(1 - t, 3);
      };

      var step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        var elapsed = timestamp - startTime;
        var t = Math.min(1, elapsed / duration);
        var eased = easeOutCubic(t);

        var val = from + (to - from) * eased;
        if (integer) val = Math.round(val);

        onUpdate(val);

        if (t < 1) {
          this[storeId] = requestAnimationFrame(step);
        } else {
          onUpdate(integer ? Math.round(to) : to);
          this[storeId] = null;
        }
      };

      this[storeId] = requestAnimationFrame(step);
    },
    formatInt: function (n) {
      return Number(n).toLocaleString();
    }
  }
};
</script>

<style scoped>
.kpi-card {
  max-width: 520px;
  border-radius: 12px;
  padding: 12px 14px;
  background: #fefefefa;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.kpi-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 10px;
}

.kpi-row + .kpi-row {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.kpi-left {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.kpi-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(242, 169, 0, 0.12);
  color: #f2a900;
}

.kpi-label {
  font-size: 0.95rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.75);
  white-space: nowrap;
}

.kpi-value {
  font-size: 1.1rem;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.85);
}
</style>
